import React, {
    useCallback,
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useRef
} from 'react';
import {useLocation} from 'react-router';

interface RouteTrackingContextType {
    currentPath: string | null;
    previousPath: string | null;
    lastVisitedLibrary: boolean;
    getBackTargetOutsideCurrentAssistant: () => string | null;
    getBackTargetForCurrentAssistantThread: () => string | null;
}

const RouteTrackingContext = createContext<
    RouteTrackingContextType | undefined
>(undefined);

interface RouteTrackingProviderProps {
    children: ReactNode;
}

const routeHistoryLimit = 50;

const getLocationPath = (location: {
    pathname: string;
    search: string;
    hash: string;
}) => `${location.pathname}${location.search}${location.hash}`;

const getPathname = (path: string) => path.split(/[?#]/)[0];

const getAssistantIdFromPath = (path: string) => {
    const match = getPathname(path).match(/^\/assistants\/([^/]+)/);

    return match?.[1] ?? null;
};

const isSameAssistantPath = (path: string, assistantId: string | null) =>
    assistantId !== null && getAssistantIdFromPath(path) === assistantId;

/**
 * The new-assistant screen stands in for an assistant that does not exist yet, so it lives under the
 * placeholder id `_` (`/assistants/_/details`, and its Import tab).
 *
 * <p>
 * It is never a back target. Once Create or Import has run, the assistant exists and that screen has
 * nothing left to return to, so offering it as the previous page sends the user forwards into a dead
 * end and then straight back to the new assistant: a loop between the two with no way out to home.
 * </p>
 */
const isNewAssistantPath = (path: string) =>
    getAssistantIdFromPath(path) === '_';

const isAssistantThreadPath = (path: string, assistantId: string | null) => {
    if (!isSameAssistantPath(path, assistantId)) {
        return false;
    }

    const pathname = getPathname(path);
    const escapedAssistantId = assistantId!.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
    );

    return new RegExp(
        `^/assistants/${escapedAssistantId}(?:/threads/[^/]+)?/?$`
    ).test(pathname);
};

export const RouteTrackingProvider: React.FC<RouteTrackingProviderProps> = ({
    children
}) => {
    const location = useLocation();
    const [currentPath, setCurrentPath] = useState<string | null>(
        location.pathname
    );
    const [previousPath, setPreviousPath] = useState<string | null>(null);

    const [lastVisitedLibrary, setLastVisitedLibrary] = useState<boolean>(
        location.pathname === '/library'
    );

    const previousPathnameRef = useRef<string | null>(null);
    const routeHistoryRef = useRef<string[]>([getLocationPath(location)]);

    useEffect(() => {
        if (previousPathnameRef.current !== location.pathname) {
            setPreviousPath(previousPathnameRef.current);
        }
        setCurrentPath(location.pathname);
        previousPathnameRef.current = location.pathname;

        const nextPath = getLocationPath(location);
        const lastPath =
            routeHistoryRef.current[routeHistoryRef.current.length - 1];
        if (lastPath !== nextPath) {
            routeHistoryRef.current = [
                ...routeHistoryRef.current,
                nextPath
            ].slice(-routeHistoryLimit);
        }

        if (location.pathname === '/') {
            setLastVisitedLibrary(false);
        } else if (location.pathname === '/library') {
            setLastVisitedLibrary(true);
        }
    }, [location]);

    const getBackTargetOutsideCurrentAssistant = useCallback(() => {
        const currentPath = getLocationPath(location);
        const currentPathname = getPathname(currentPath);
        const currentAssistantId = getAssistantIdFromPath(currentPath);

        for (
            let index = routeHistoryRef.current.length - 2;
            index >= 0;
            index--
        ) {
            const candidate = routeHistoryRef.current[index];

            if (isNewAssistantPath(candidate)) {
                continue;
            }

            if (isSameAssistantPath(candidate, currentAssistantId)) {
                continue;
            }

            if (getPathname(candidate) === currentPathname) {
                continue;
            }

            return candidate;
        }

        return null;
    }, [location]);

    const getBackTargetForCurrentAssistantThread = useCallback(() => {
        const currentPath = getLocationPath(location);
        const currentPathname = getPathname(currentPath);
        const currentAssistantId = getAssistantIdFromPath(currentPath);

        for (
            let index = routeHistoryRef.current.length - 2;
            index >= 0;
            index--
        ) {
            const candidate = routeHistoryRef.current[index];

            if (isNewAssistantPath(candidate)) {
                continue;
            }

            if (!isAssistantThreadPath(candidate, currentAssistantId)) {
                continue;
            }

            if (getPathname(candidate) === currentPathname) {
                continue;
            }

            return candidate;
        }

        return null;
    }, [location]);

    return (
        <RouteTrackingContext.Provider
            value={{
                currentPath,
                previousPath,
                lastVisitedLibrary,
                getBackTargetOutsideCurrentAssistant,
                getBackTargetForCurrentAssistantThread
            }}
        >
            {children}
        </RouteTrackingContext.Provider>
    );
};

export const useRouteTracker = (): RouteTrackingContextType => {
    const context = useContext(RouteTrackingContext);
    if (context === undefined) {
        throw new Error(
            'useRouteTracker must be used within a RouteTrackingProvider'
        );
    }
    return context;
};
